import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * ThemeDiscoveryWizard
 * Replaces the simple textarea for "Theme Ideas" with a rich selection flow.
 * 
 * Flow:
 * 1. Mode Selection: "Browse Ideas", "I have a concept", "Surprise Me"
 * 2. Browse Categories: Show high-level categories (fetched or derived)
 * 3. Browse Templates: Show grid of ThemeTemplates filtered by category
 * 4. Concept: Show textarea (legacy behavior but nicer UI)
 */
const EVENT_TYPE_SURPRISE_PROMPTS = {
    'Kids Birthday': [
        'Surprise me! Dinosaur Safari & Jurassic Excavation Adventure with earthy jungle tones',
        'Surprise me! Outer Space Cosmic Galaxy Astronaut Voyage with star glow lighting',
        'Surprise me! Fairytale Enchanted Forest Storybook Wonderland with pastel moss accents',
        'Surprise me! Deep Ocean Mermaid & Coral Reef Underwater Splash'
    ],
    'Birthday Party': [
        'Surprise me! Vibrant Disco Glam & Neon Studio 54 celebration with mirror ball glow',
        'Surprise me! Mid-Century Palm Springs Poolside Lounge with warm retro citrus accents',
        'Surprise me! Velvet & Brass 1920s Speakeasy Cocktail Soirée with moody warm lighting',
        'Surprise me! Modern Minimalist Rooftop Lounge with sunset golden hour ambience'
    ],
    'Baby Shower': [
        'Surprise me! Cloud Nine Celestial High Tea with soft pastel florals & macarons',
        'Surprise me! Whimsical Meadow Botanicals & Sweet Floral Spritz celebration',
        'Surprise me! Storybook Honey Harvest & Teddy Bear Picnic with warm wooden accents',
        'Surprise me! Parisian Patisserie Morning Brunch with blush blossoms'
    ],
    'Dinner Party': [
        'Surprise me! Candlelit Tuscan Garden Trattoria with rustic linen and citrus table runner',
        'Surprise me! Moody Parisian Bistro with vintage candle holders and jazz ambience',
        'Surprise me! Spanish Tapas & Sangria Courtyard Fiesta with terracotta accents',
        'Surprise me! Autumn Farm-to-Table Harvest with roasted heirloom warmth'
    ],
    'Anniversary': [
        'Surprise me! Golden Hour Milestone Elegance with champagne toast and romantic florals',
        'Surprise me! Starlit Botanical Greenhouse with ambient fairy lights and vintage lace',
        'Surprise me! Seaside Mediterranean Sunset with breezy coastal candlelight'
    ],
    'Graduation': [
        'Surprise me! Celestial Next Chapter Horizons with black & gold luminary styling',
        'Surprise me! Modern Minimalist Rooftop Milestone with festival lounge vibes'
    ],
    'Holiday Party': [
        'Surprise me! Cozy Winter Chalet & Warm Hearth Glow with evergreen eucalyptus',
        'Surprise me! Modern Midnight Sparkle Holiday Mixer with festive shimmer'
    ]
};

const ThemeDiscoveryWizard = ({
    onSelectTheme,
    currentInput,
    onInputChange,
    onNext,
    eventType,
    ageRange,
    view = 'selection', // 'selection', 'browse_categories', 'browse_templates', 'custom'
    onViewChange
}) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [surpriseIndex, setSurpriseIndex] = useState(0);
    const [lastCustomInput, setLastCustomInput] = useState(
        currentInput?.includes('Theme Mode:') || currentInput?.includes('Surprise me!') ? '' : (currentInput || '')
    );

    const getNextSurprisePrompt = () => {
        const normType = eventType || 'Birthday Party';
        const pool = EVENT_TYPE_SURPRISE_PROMPTS[normType] || 
            (normType.toLowerCase().includes('kid') ? EVENT_TYPE_SURPRISE_PROMPTS['Kids Birthday'] :
             normType.toLowerCase().includes('baby') ? EVENT_TYPE_SURPRISE_PROMPTS['Baby Shower'] :
             normType.toLowerCase().includes('dinner') ? EVENT_TYPE_SURPRISE_PROMPTS['Dinner Party'] :
             normType.toLowerCase().includes('anniversary') ? EVENT_TYPE_SURPRISE_PROMPTS['Anniversary'] :
             EVENT_TYPE_SURPRISE_PROMPTS['Birthday Party']);
        
        const nextIdx = (surpriseIndex + 1) % pool.length;
        setSurpriseIndex(nextIdx);
        return pool[nextIdx];
    };

    const mode = view;
    const setMode = onViewChange || (() => {});

    // Reset internal state when view changes
    useEffect(() => {
        if (view === 'selection' || view === 'browse_categories') {
            setSelectedCategory(null);
        }
    }, [view]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (eventType && eventType !== 'Other') params.eventType = eventType;
            if (ageRange) {
                params.age = Array.isArray(ageRange) ? ageRange.join(',') : ageRange;
            }

            const response = await axios.get('/api/catalog/themes', { params });
            const fetchedTemplates = response.data || [];
            setTemplates(fetchedTemplates);

            const catMap = {};
            fetchedTemplates.forEach(t => {
                const tags = t.tags || [];
                if (tags.length === 0) {
                    if (!catMap['General']) catMap['General'] = { count: 0, image: t.image_url };
                    catMap['General'].count++;
                } else {
                    tags.forEach(tag => {
                        if (!catMap[tag]) catMap[tag] = { count: 0, image: t.image_url };
                        catMap[tag].count++;
                    });
                }
            });

            const derivedCats = Object.entries(catMap).map(([name, data]) => ({
                id: name,
                name: name.charAt(0).toUpperCase() + name.slice(1),
                count: data.count,
                image: data.image
            })).sort((a, b) => b.count - a.count);

            // Always add 'All Themes' if we have templates
            if (fetchedTemplates.length > 0) {
                derivedCats.unshift({ id: 'All', name: 'All Themes', count: fetchedTemplates.length, image: fetchedTemplates[0].image_url });
            }

            setCategories(derivedCats);
        } catch (err) {
            console.error("Failed to fetch theme templates", err);
            setError("We couldn't load the theme ideas right now.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch templates once
    useEffect(() => {
        if ((mode === 'browse_categories' || mode === 'browse_templates') && templates.length === 0) {
            fetchTemplates();
        }
    }, [mode, eventType, templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCategorySelect = (catId) => {
        setSelectedCategory(catId);
        setMode('browse_templates');
    };

    const handleTemplateSelect = (template) => {
        const text = `Theme: ${template.name}. ${template.description}. Mood: ${template.mood}.`;
        onInputChange(text);
        setLastCustomInput(text);
        onNext(); // Auto-advance when template picked
    };

    const filteredTemplates = selectedCategory
        ? (selectedCategory === 'All' ? templates : templates.filter(t => (t.tags || []).includes(selectedCategory) || (selectedCategory === 'General' && (!t.tags || t.tags.length === 0))))
        : templates;

    if (mode === 'selection') {
        const isSurprise = currentInput?.includes('Surprise me!') || currentInput === 'Theme Mode: Surprise Me';
        const isBrowse = currentInput === 'Theme Mode: Browsing...' || currentInput?.startsWith('Theme:');
        const isCustom = currentInput === 'Theme Mode: Custom Concept' || (!isSurprise && !isBrowse && !!currentInput);

        return (
            <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                <div className="text-center space-y-2 mb-6 sm:mb-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">How should we pick a theme?</h3>
                    <p className="text-sm text-gray-500">Choose an approach that works best for you.</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">

                    <button
                        type="button"
                        onClick={() => setMode('custom')}
                        className={`relative group p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center justify-center gap-3 ${isCustom ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-100 bg-white hover:border-blue-300 hover:shadow-lg'}`}
                    >
                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">💡</div>
                        <div>
                            <div className="text-base font-bold text-gray-900">I Have a Concept</div>
                            <div className="text-xs text-gray-500">Describe your specific vision</div>
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setMode('surprise');
                            const nextPrompt = getNextSurprisePrompt();
                            onInputChange(nextPrompt);
                        }}
                        className={`relative group p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 text-center flex flex-col items-center justify-center gap-3 ${isSurprise ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-100 bg-white hover:border-amber-300 hover:shadow-lg'}`}
                    >
                        <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">🎁</div>
                        <div>
                            <div className="text-base font-bold text-gray-900">Surprise Me</div>
                            <div className="text-xs text-gray-500">
                                {isSurprise ? (
                                    <span className="text-amber-700 font-semibold block mt-0.5">
                                        Tailored for {eventType || 'your event'}
                                    </span>
                                ) : (
                                    'AI suggests creative ideas for your event'
                                )}
                            </div>
                        </div>
                        {isSurprise && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        )}
                        {isSurprise && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const nextPrompt = getNextSurprisePrompt();
                                    onInputChange(nextPrompt);
                                }}
                                className="mt-2 text-[11px] font-bold px-2.5 py-1 rounded-lg bg-amber-100/80 hover:bg-amber-200 text-amber-900 flex items-center gap-1 transition active:scale-95 shadow-2xs"
                                title="Click to roll another theme inspiration for this event type"
                            >
                                <span>🎲</span> Re-Roll Archetype
                            </div>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    if (mode === 'browse_categories') {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="text-center space-y-2 mb-6">
                    <h3 className="text-xl font-bold text-gray-900">What's the vibe?</h3>
                    <p className="text-sm text-gray-500">Pick a category to explore templates.</p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map(n => (
                            <div key={n} className="h-32 bg-gray-100 rounded-xl animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => handleCategorySelect(cat.id)}
                                className="group relative h-32 rounded-xl overflow-hidden border-2 border-transparent hover:border-primary transition-all shadow-sm hover:shadow-md"
                            >
                                <img 
                                    src={cat.image} 
                                    alt={cat.name} 
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1513151233558-d860c5398176?q=80&w=500&auto=format&fit=crop';
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                <div className="absolute bottom-3 left-3 text-left">
                                    <div className="text-white font-bold text-sm">{cat.name}</div>
                                    <div className="text-white/70 text-[10px]">{cat.count} ideas</div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (mode === 'browse_templates') {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setMode('browse_categories')} className="text-sm font-bold text-primary flex items-center gap-1 hover:underline">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        Back to Categories
                    </button>
                    <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{selectedCategory}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {filteredTemplates.map((template) => (
                        <button
                            key={template.id}
                            onClick={() => handleTemplateSelect(template)}
                            className="flex flex-col text-left rounded-xl border-2 border-gray-100 hover:border-primary hover:shadow-md transition-all overflow-hidden bg-white"
                        >
                            <div className="h-32 w-full relative">
                                <img 
                                    src={template.image_url} 
                                    alt={template.name} 
                                    className="absolute inset-0 w-full h-full object-cover" 
                                    onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1513151233558-d860c5398176?q=80&w=500&auto=format&fit=crop';
                                    }}
                                />
                            </div>
                            <div className="p-4">
                                <div className="text-sm font-bold text-gray-900 mb-1">{template.name}</div>
                                <div className="text-xs text-gray-500 whitespace-normal break-words">{template.description}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (mode === 'custom') {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="text-center space-y-2 mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Tell us about your concept <span className="text-red-500">*</span></h3>
                    <p className="text-sm text-gray-500">Describe the theme, mood, or specific elements you have in mind.</p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-blue-100 p-1 shadow-inner">
                    <textarea
                        value={lastCustomInput}
                        onChange={(e) => {
                            setLastCustomInput(e.target.value);
                            onInputChange(e.target.value);
                        }}
                        placeholder="e.g., A magical woodland forest with glowing mushrooms and fairy lights..."
                        className="w-full h-40 p-5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none transition resize-none text-base leading-relaxed"
                    />
                </div>
            </div>
        );
    }

    return null;
};

export default ThemeDiscoveryWizard;
